/**
 * QuantumFlowSync.AI - Authentication Context
 * Manages employee authentication state and JWT tokens
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'));
  const navigate = useNavigate();

  // Load employee profile on mount
  useEffect(() => {
    if (accessToken) {
      loadEmployee();
    } else {
      setLoading(false);
    }
  }, [accessToken]);

  // Auto-refresh token before expiry
  useEffect(() => {
    if (!accessToken) return;

    // Refresh token every 10 minutes (access token expires in 15 min)
    const interval = setInterval(() => {
      refreshAccessToken();
    }, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [accessToken]);

  const loadEmployee = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired, try to refresh
          await refreshAccessToken();
          return;
        }
        throw new Error('Failed to load employee');
      }

      const data = await response.json();
      setEmployee(data);
    } catch (error) {
      console.error('Failed to load employee:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, twoFaCode = null) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password, two_fa_code: twoFaCode })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();

    // If 2FA required, return status
    if (data.two_fa_required) {
      return { twoFaRequired: true };
    }

    // Store tokens
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    setAccessToken(data.access_token);
    setRefreshToken(data.refresh_token);
    setEmployee(data.employee);

    return { twoFaRequired: false };
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear local state
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setAccessToken(null);
    setRefreshToken(null);
    setEmployee(null);
    navigate('/login');
  };

  const refreshAccessToken = async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      localStorage.setItem('access_token', data.access_token);
      setAccessToken(data.access_token);
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await fetch('/api/auth/change-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Password change failed');
    }

    return await response.json();
  };

  const setup2FA = async () => {
    const response = await fetch('/api/auth/2fa/setup', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error('2FA setup failed');
    }

    return await response.json();
  };

  const enable2FA = async (code) => {
    const response = await fetch('/api/auth/2fa/enable', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({ code })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '2FA enable failed');
    }

    // Reload employee to get updated 2FA status
    await loadEmployee();
    return await response.json();
  };

  const disable2FA = async (password) => {
    const response = await fetch('/api/auth/2fa/disable', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({ password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '2FA disable failed');
    }

    // Reload employee to get updated 2FA status
    await loadEmployee();
    return await response.json();
  };

  const forgotPassword = async (email) => {
    const response = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Password reset request failed');
    }

    return await response.json();
  };

  const resetPassword = async (token, newPassword) => {
    const response = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token, new_password: newPassword })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Password reset failed');
    }

    return await response.json();
  };

  const hasPermission = (resource, scope, action) => {
    if (!employee || !employee.permissions) return false;
    
    const permissionString = `${resource}:${scope}:${action}`;
    const wildcardResource = `${resource}:*:${action}`;
    const wildcardScope = `${resource}:${scope}:*`;
    const fullWildcard = `${resource}:*:*`;
    
    return employee.permissions.some(p => 
      p === permissionString || 
      p === wildcardResource || 
      p === wildcardScope || 
      p === fullWildcard
    );
  };

  const hasTier = (maxTier) => {
    if (!employee || !employee.roles) return false;
    return employee.roles.some(role => role.tier <= maxTier);
  };

  const hasRole = (roleName) => {
    if (!employee || !employee.roles) return false;
    return employee.roles.some(role => role.name === roleName);
  };

  // Generic API request helper with authentication
  const apiRequest = async (url, options = {}) => {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          ...options.headers
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired, try to refresh
          await refreshAccessToken();
          // Retry the request with new token
          return apiRequest(url, options);
        }
        const error = await response.json();
        return { success: false, error: error.detail || 'Request failed' };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('API request error:', error);
      return { success: false, error: error.message || 'Network error' };
    }
  };

  const value = {
    employee,
    loading,
    accessToken,
    isAuthenticated: !!employee,
    login,
    logout,
    changePassword,
    setup2FA,
    enable2FA,
    disable2FA,
    forgotPassword,
    resetPassword,
    hasPermission,
    hasTier,
    hasRole,
    refreshAccessToken,
    apiRequest
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
